/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { RGBAColor, Position as DeckGlPosition } from '@deck.gl/core';
import type { Polygon, Feature, GeoJsonProperties, MultiPolygon } from 'geojson'; // as some type export moved to separate package since v7
import { PolygonLayer, TextLayer } from '@deck.gl/layers';
import { point, featureCollection, polygon } from '@turf/helpers';
import envelope from '@turf/envelope';
import buffer from '@turf/buffer';
import centroid from '@turf/centroid';
import transformRotate from '@turf/transform-rotate';
import { GeoJSON, SensorLatest } from '../../../services/api';
import { findNearestPointsWithTurf, hexToRgb, SpaceParams } from '../mapHelpers';
import { themeProps } from '../../../styles/theme';
import chartColors from '../../../utils/chartColors';

interface AreaMarker {
  id: string;
  contours: DeckGlPosition[];
  color: string;
}

const getAreaCoverageLayer = (
  sensorsById: Map<string, SensorLatest>,
  spaces: SpaceParams[],
  floorPlan: GeoJSON,
  floorPlanBearing: number
) => {
  const areaPolygons = new Map<string, AreaMarker>();

  // find a nearest position outside the floorplan
  const findPosition = (areaPositions: DeckGlPosition[]) => {
    let targetPosition;
    const floorPolygon = floorPlan.features.find((item) => item.properties.type === 'area.floor');
    const floorPositions = floorPolygon.geometry.coordinates[0][0] as DeckGlPosition[];
    const centerofSpace = centroid(polygon([areaPositions]));
    const nearestPosition = findNearestPointsWithTurf(
      centerofSpace.geometry.coordinates as DeckGlPosition,
      floorPositions
    );
    if (nearestPosition) targetPosition = nearestPosition;
    return targetPosition;
  };

  // we need to fetch from api instead for demoGrouped Sensors

  if (spaces) {
    spaces.forEach((space) => {
      const { title, sensors } = space;
      const turfPoints: any[] = [];
      const testPoints: any[] = [];
      sensors.forEach((sensorId) => {
        const pos = sensorsById.get(sensorId)?.position;
        const lat = pos?.lat;
        const lng = pos?.lng;
        if (lat && lng) {
          turfPoints.push(point([lng, lat, 1]));
          testPoints.push({ longitude: lat, latitude: lng });
        }
      });

      // Rotate geometry BEFORE creating envelope for proper alignment
      function createAlignedEnvelope(features: any, angle: number, spacing = 1) {
        // Get centroid as rotation pivot
        const centroids = centroid(features);
        // Rotate the original geometry by negative angle
        const rotatedGeometry = transformRotate(features, -angle, { pivot: centroids });
        // Create axis-aligned envelope of rotated geometry
        const axisAlignedEnvelope = envelope(rotatedGeometry);
        // Rotate envelope back to desired orientation
        const rotatedPolygon = transformRotate(axisAlignedEnvelope, angle, { pivot: centroids });
        let modifiedPolygon: Feature<Polygon | MultiPolygon, GeoJsonProperties> = rotatedPolygon;
        if (spacing && spacing > 0 && rotatedPolygon) {
          // Convert meters to kilometers for turf buffer
          const spacingKm = spacing / 1000;
          const buffered = buffer(modifiedPolygon, spacingKm);
          if (buffered) modifiedPolygon = buffered;
        }
        return modifiedPolygon;
      }

      const alignedEnvelope =
        turfPoints.length > 0 &&
        createAlignedEnvelope(featureCollection(turfPoints), floorPlanBearing);

      if (alignedEnvelope) {
        const areaData: AreaMarker = {
          id: title,
          contours: alignedEnvelope.geometry.coordinates[0] as DeckGlPosition[],
          color:
            areaPolygons.size === 0
              ? chartColors[0]
              : chartColors[areaPolygons.size % chartColors.length],
        };

        areaPolygons.set(title, areaData);
      }
    });
  }

  const polygonLayer = new PolygonLayer({
    id: 'polygon-area-layer',
    data: Array.from(areaPolygons.values()),
    stroked: true,
    filled: true,
    extruded: false,
    wireframe: true,
    lineWidthMinPixels: 1,
    // @ts-ignore
    getPolygon: (x: AreaMarker) => x.contours,
    getLineColor: [0, 0, 0, 255],
    // getFillColor: [255, 192, 203, 255],
    // @ts-ignore
    getFillColor: (x: AreaMarker): RGBAColor => hexToRgb(x.color, 128),
    getLineWidth: 0,
  });

  // Add a text layer to display titles
  const textLayer = new TextLayer({
    id: 'polygon-text-layer',
    data: Array.from(areaPolygons.values()),
    pickable: true,
    // @ts-ignore
    getPosition: (d: AreaMarker) => {
      // Calculate centroid of the polygon for text positioning
      const { contours } = d;
      if (contours && contours.length > 0) {
        const targetPosition = findPosition(contours) ?? [0, 0];
        return targetPosition;
      }
      return [0, 0];
    },
    // @ts-ignore
    getText: (d: AreaMarker) => d.id, // Adjust based on your data structure
    getSize: 0.75,
    getAngle: 0,
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    sizeScale: 0.5,
    sizeUnits: 'meters',
    backgroundPadding: [10, 10, 10, 10],
    getColor: [0, 0, 0, 255], // Black text
    // @ts-ignore
    getBackgroundColor: (x: AreaMarker): RGBAColor => hexToRgb(x.color, 128),
    background: true,
    billboard: true,
    fontFamily: themeProps.fontFamily.body,
  });

  return [polygonLayer, textLayer];
};

export default getAreaCoverageLayer;
